import axios from 'axios';
import { auth, db } from '../firebase'; // Importar Firestore y auth
import { addDoc, collection, doc, getDoc, query, where, getDocs, setDoc, deleteDoc } from 'firebase/firestore'; // Funciones para Firestore
import { serverTimestamp } from 'firebase/firestore';
import { orderBy } from 'firebase/firestore'; // Asegúrate de importar orderBy

let API_KEY = localStorage.getItem('API_KEY') || ''; // Intentar obtener la API_KEY del localStorage

// Función para obtener la API Key desde Firestore
const getUserApiKey = async (uid) => {
  const userDocRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userDocRef);
  if (userDoc.exists()) {
    const data = userDoc.data(); 
    
    // Verificamos si el usuario tiene un email específico para aplicar la limitación
    const email = data.email;
    if (email === 'sgimenez@mktmarketingdigital.com') {
      return { apiKey: data.API_KEY, isLimited: true };
    } else {
      return { apiKey: data.API_KEY, isLimited: false };
    }
  } else {
    throw new Error("No se encontró el documento del usuario"); 
  }
};

// Nueva función para verificar el gasto diario
const checkAndUpdateDailyLimit = async (uid, cost) => {
  const userDocRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userDocRef);

  if (userDoc.exists()) {
    const data = userDoc.data();
    const today = new Date().toISOString().split('T')[0]; // Fecha actual en formato YYYY-MM-DD
    let dailySpending = data.dailySpending || {}; // Obtenemos el objeto de gasto diario

    // Verificamos si hay un registro para el día de hoy
    if (dailySpending[today]) {
      // Actualizamos el gasto del día de hoy
      dailySpending[today] += cost;
    } else {
      // Si no hay gasto registrado para hoy, comenzamos desde cero
      dailySpending[today] = cost;
    }

    // Verificamos si se ha excedido el límite
    if (dailySpending[today] > 2) {
      throw new Error('Has excedido tu límite diario de consultas');
    }
     else {
    }

    // Guardamos el nuevo gasto en Firestore
    await setDoc(userDocRef, { dailySpending }, { merge: true });
  }
};
auth.onAuthStateChanged(async (user) => {
  if (user) {
    // Usuario ha iniciado sesión, actualiza la API_KEY
    try {
      const { apiKey } = await getUserApiKey(user.uid);
      API_KEY = apiKey;
      localStorage.setItem('API_KEY', apiKey);
    } catch (error) {
      console.error("Error al obtener la API_KEY del usuario:", error);
    }
  } else {
    // Usuario ha cerrado sesión, elimina la API_KEY
    API_KEY = '';
    localStorage.removeItem('API_KEY');
  }
});


// Crear una instancia de Axios
const axiosInstance = axios.create({
  baseURL: 'https://api.openai.com/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 segundos
});


// Interceptor para agregar la API Key en cada solicitud
axiosInstance.interceptors.request.use(
  (config) => {
    if (API_KEY) {
      config.headers.Authorization = `Bearer ${API_KEY}`;
    } else {
      console.error('API_KEY no está disponible');
      throw new Error('API_KEY no está disponible');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default axiosInstance;

// Define la función de limpieza globalmente
const cleanAssistantResponse = (text) => {
  // Reemplaza patrones como "【4:1†source】" por "[1]"
  return text.replace(/【\d+:\d+†source】/g, '[1]');
};

axiosInstance.interceptors.response.use(
  (response) => {
    // Limpiar referencias de sources en output_text (Responses API)
    if (response.data && response.data.output_text) {
      response.data.output_text = cleanAssistantResponse(response.data.output_text);
    }
    // Legacy: chat completions
    if (response.data && response.data.choices) {
      response.data.choices.forEach(choice => {
        if (choice.message && choice.message.content) {
          choice.message.content = cleanAssistantResponse(choice.message.content);
        }
      });
    }
    return response;
  },
  (error) => Promise.reject(error)
);


// Función para obtener detalles del asistente desde Firestore
export const getAssistantDetails = async (assistantId) => {
  try {
    const assistantRef = doc(db, 'assistants', assistantId);
    const assistantDoc = await getDoc(assistantRef);
    if (assistantDoc.exists()) {
      return assistantDoc.data();
    }
    throw new Error("Asistente no encontrado en Firestore");
  } catch (error) {
    console.error("Error obteniendo los detalles del asistente:", error);
    throw error;
  }
};
// Helper interno para llamar a la Responses API
const callResponsesAPI = async ({ input, instructions, model, apiKey, tools }) => {
  const url = 'https://api.openai.com/v1/responses';
  const headers = {
    'Authorization': `Bearer ${apiKey || API_KEY}`,
    'Content-Type': 'application/json',
  };

  const payload = {
    model,
    instructions,
    input,
    store: true,
  };

  if (tools?.length) {
    payload.tools = tools;
  }

  const response = await axios.post(url, payload, { headers });
  return {
    text: response.data.output_text || '',
    responseId: response.data.id,
  };
};

const VALID_ROLES_FRONT = new Set(['user', 'assistant', 'system', 'developer']);

function sanitizeInputItemFront(msg) {
  if (!msg || typeof msg !== 'object') return null;
  let role = msg.role;
  if (role === 'agent') role = 'assistant';
  if (!VALID_ROLES_FRONT.has(role)) {
    console.warn(`[openaiService] Rol inválido ignorado: "${msg.role}"`);
    return null;
  }
  let content = msg.content;
  if (typeof content !== 'string') {
    if (Array.isArray(content)) return { role, content };
    if (content === null || content === undefined) return null;
    content = String(content);
  }
  if (!content.trim()) return null;
  return { role, content };
}

// Función para obtener la respuesta del asistente usando Responses API
export const getChatGPTResponseWithAssistant = async (assistantId, messages) => {
  try {
    const assistantDetails = await getAssistantDetails(assistantId);
    if (!assistantDetails) return "Error al obtener detalles del asistente.";

    const input = messages
      .map(sanitizeInputItemFront)
      .filter(Boolean)
      .slice(-20);

    const tools = [];
    const vectorStoreId = assistantDetails.vectorStoreId ||
      assistantDetails.tool_resources?.file_search?.vector_store_ids?.[0];

    if (vectorStoreId) {
      tools.push({ type: 'file_search', vector_store_ids: [vectorStoreId] });
    }

    const response = await callResponsesAPI({
      input,
      instructions: assistantDetails.instructions || '',
      model: assistantDetails.model || 'gpt-4o-mini-2024-07-18',
      apiKey: API_KEY,
      tools: tools.length ? tools : undefined,
    });

    return response.text || "No se recibió una respuesta válida del asistente.";
  } catch (error) {
    console.error("Error obteniendo respuesta del asistente:", error.response ? error.response.data : error);
    return "Error al obtener respuesta del asistente.";
  }
};



export const createAssistantInFirestore = async (assistantData, assistantId) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Usuario no autenticado.');
    }
    const userId = user.uid;

    // Agregar el ownerId, assistantId y createdAt a los datos del asistente
    const assistantDataWithOwner = {
      name: assistantData.name,
      instructions: assistantData.instructions,
      allowedUsers: assistantData.allowedUsers,
      ownerId: userId,
      assistantId: assistantId,
      model: assistantData.model || 'gpt-4o-mini',
      tools: assistantData.tools || [],
      tool_resources: assistantData.tool_resources || {},
      createdAt: assistantData.createdAt, // Incluir createdAt
    };

    // Guardar el asistente en Firestore con assistantId como ID del documento
    const assistantRef = doc(db, 'assistants', assistantId);
    await setDoc(assistantRef, assistantDataWithOwner);

    return assistantId; // Retornar el ID del asistente creado
  } catch (e) {
    console.error('Error agregando documento: ', e);
    throw e;
  }
};



// Función para eliminar un asistente (solo Firestore, OpenAI Assistants API está deprecada)
export const deleteAssistant = async (assistantId) => {
  try {
    const assistantRef = doc(db, 'assistants', assistantId);
    await deleteDoc(assistantRef);
    return { id: assistantId, deleted: true };
  } catch (error) {
    console.error(`Error al eliminar el asistente ${assistantId}:`, error);
    throw error;
  }
};

// Función para eliminar una knowledge base (vector store)
export const deleteKnowledgeBase = async (vectorStoreId) => {
  const url = `https://api.openai.com/v1/vector_stores/${vectorStoreId}`;

  const headers = {
    'Authorization': `Bearer ${API_KEY}`,
    'Content-Type': 'application/json',
  };

  try {
    const response = await axios.delete(url, { headers });
    return response.data;
  } catch (error) {
    console.error(`Error al eliminar la Knowledge Base ${vectorStoreId}:`, error.response ? error.response.data : error);
    throw error;
  }
};

// Función para eliminar un archivo del vector store
export const deleteFileFromVectorStore = async (vectorStoreId, fileId) => {
  const url = `https://api.openai.com/v1/vector_stores/${vectorStoreId}/files/${fileId}`;

  const headers = {
    'Authorization': `Bearer ${API_KEY}`,
    'Content-Type': 'application/json',
  };

  try {
    const response = await axios.delete(url, { headers });
    return response.data;
  } catch (error) {
    console.error(`Error al eliminar el archivo ${fileId}:`, error.response ? error.response.data : error);
    throw error;
  }
};


const fetchAssistantImage = async (assistantId) => {
  const userDocRef = doc(db, 'assistants', assistantId);
  const userDoc = await getDoc(userDocRef);
  
  if (userDoc.exists()) {
    const data = userDoc.data();
    return data.image; // Retorna la imagen guardada
  }
  return null; // Si no existe, devuelve null
};


// Función para obtener todos los asistentes desde Firebase Firestore filtrados por allowedUsers
export const getAllAssistants = async (currentUserUid) => {
  try {
    if (!currentUserUid) {
      console.error('currentUserUid is undefined in getAllAssistants');
      return [];
    }

    const assistantsRef = collection(db, 'assistants');
    const q = query(
      assistantsRef, 
      where('allowedUsers', 'array-contains', currentUserUid),
      orderBy('createdAt', 'desc') // Ordenar por fecha de creación descendente
    );
    const assistantsSnapshot = await getDocs(q);
    const assistants = assistantsSnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        instructions: data.instructions || 'No se han configurado instrucciones.',
        image: data.image || null,
        allowedUsers: data.allowedUsers || [],
        createdAt: data.createdAt, // Incluir createdAt
        phone_number_id: data.phone_number_id, // Incluir createdAt
      };
    });
    return assistants;
  } catch (error) {
    console.error('Error obteniendo asistentes:', error);
    return [];
  }
};



// Función para crear un asistente (ahora solo en Firestore, OpenAI Assistants API está deprecada)
export const createAssistantInOpenAI = async (assistantData) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Usuario no autenticado.');

    // Generar un ID localmente (ya no se crea en OpenAI)
    const newAssistantRef = doc(collection(db, 'assistants'));
    const assistantId = newAssistantRef.id;

    // Obtener el prompt oculto
    const hiddenPrompt = await getHiddenPrompt();

    // Combinar el prompt oculto con las instrucciones del usuario
    const combinedInstructions = `${hiddenPrompt}\n\n${assistantData.instructions}`;

    const dataToSave = {
      name: assistantData.name,
      instructions: combinedInstructions,
      allowedUsers: assistantData.allowedUsers,
      ownerId: user.uid,
      assistantId: assistantId,
      model: assistantData.model || 'gpt-4o-mini',
      tools: assistantData.tools || [],
      tool_resources: assistantData.tool_resources || {},
      createdAt: assistantData.createdAt || serverTimestamp(),
    };

    await setDoc(newAssistantRef, dataToSave);
    return { id: assistantId, ...dataToSave };
  } catch (error) {
    console.error("Error al crear el asistente:", error);
    return null;
  }
};
// Define el hiddenPrompt en este archivo para reutilizarlo
export const getHiddenPrompt = async () => {
  // Definir el prompt oculto aquí
  const hiddenPrompt = `
`;
  return hiddenPrompt;
};

// Función para listar archivos subidos
export const getAllKnowledgeBases = async () => {
  const url = 'https://api.openai.com/v1/files'; 
  
  const headers = {
    'Authorization': `Bearer ${API_KEY}`,
    'Content-Type': 'application/json',
 
  };

  try {
    const response = await axios.get(url, { headers });
    return response.data.data; 
  } catch (error) {
    console.error('Error obteniendo las Knowledge Bases:', error.response ? error.response.data : error);
    return [];
  }
};

// Función para crear una Knowledge Base sin archivos (solo con el nombre)
export const createKnowledgeBase = async (name) => {
  const url = 'https://api.openai.com/v1/vector_stores'; // URL corregida

  const headers = {
    'Authorization': `Bearer ${API_KEY}`,
    'Content-Type': 'application/json',

  };

  const data = {
    name: name,  // Solo el nombre de la Knowledge Base
  };

  try {
    const response = await axios.post(url, data, { headers });
    return response.data;
  } catch (error) {
    console.error("Error al crear Knowledge Base:", error.response ? error.response.data : error);
    return null;
  }
};

// Función para obtener todas las vector stores
export const getAllVectorStores = async (currentUserUid) => {
  try {
    if (!currentUserUid) {
      console.error('currentUserUid is undefined in getAllVectorStores');
      return [];
    }

    const basesRef = collection(db, 'bases');
    const q = query(
      basesRef,
      where('allowedUsers', 'array-contains', currentUserUid),
      orderBy('createdAt', 'desc') // Ordenar por fecha de creación descendente
    );
    const basesSnapshot = await getDocs(q);
    const bases = basesSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      allowedUsers: doc.data().allowedUsers || [],
      createdAt: doc.data().createdAt, // Incluir createdAt
    }));
    return bases;
  } catch (error) {
    console.error('Error obteniendo las bases de conocimiento:', error);
    return [];
  }
};

export const uploadFilesToKnowledgeBase = async (files) => {
  const url = 'https://api.openai.com/v1/files';
  const headers = {
    'Authorization': `Bearer ${API_KEY}`,
  };

  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
  const MAX_FILES = 5;

  if (files.length > MAX_FILES) {
    throw new Error(`No puedes subir más de ${MAX_FILES} archivos a la vez.`);
  }

  const uploadedFiles = [];

  for (let file of files) {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`El archivo ${file.name} supera el tamaño máximo permitido de 50 MB.`);
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('purpose', 'assistants');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Error: ${errorData.error?.message || response.statusText}`);
      }

      const data = await response.json();
      uploadedFiles.push(data);  // Añadir el archivo subido a la lista con su ID (fileId)
    } catch (error) {
      console.error(`Error subiendo el archivo ${file.name}:`, error);
      throw error;
    }
  }

  return uploadedFiles; // Devuelve todos los archivos subidos con sus fileId
};


// Función para añadir múltiples archivos a un vector store
// Función para añadir múltiples archivos a un vector store
// Función para añadir múltiples archivos a un vector store
export const addFileToVectorStore = async (vector_store_id, uploadedFiles) => {
  const url = `https://api.openai.com/v1/vector_stores/${vector_store_id}/files`;

  const headers = {
    'Authorization': `Bearer ${API_KEY}`,
    'Content-Type': 'application/json',

  };

  // Función para manejar el reintento en caso de conflicto
  const addFileWithRetry = async (file, attempt = 1) => {
    const MAX_ATTEMPTS = 3; // Número máximo de intentos

    if (!file.id) {
      console.error(`El archivo subido no contiene un 'id'. Verifica la respuesta del servidor para: `, file);
      // Lanzar error directo, pues no hay nada que reintentar
      throw new Error(`Archivo ${file.name || '(desconocido)'} no tiene ID en la respuesta de OpenAI`);
    }

    const data = {
      file_id: file.id,
      chunking_strategy: { type: 'auto' },
    };

    try {
      const response = await axios.post(url, data, { headers });
      return response.data;
    } catch (error) {
      // Si es un conflicto (409) y no hemos agotado intentos, reintenta
      if (error.response && error.response.status === 409 && attempt < MAX_ATTEMPTS) {
        console.warn(`Conflicto al añadir el archivo ${file.id}, reintentando (${attempt + 1}/${MAX_ATTEMPTS})...`);
        await new Promise(resolve => setTimeout(resolve, 2000)); // Esperar 2 segundos
        return addFileWithRetry(file, attempt + 1);
      } else {
        // Aquí es donde SÍ lanzas la excepción en lugar de retornar null
        console.error(`Error añadiendo el archivo ${file.id} al vector store:`, error.response ? error.response.data : error);
        throw error; // <--- LANZAR
      }
    }
  };

  // Promise.all para procesar todos los archivos
  // Si cualquier archivo da error, saltará al catch del componente
  const results = await Promise.all(
    uploadedFiles.map(file => addFileWithRetry(file))
  );

  return results; // Devolver los resultados si todo fue bien
};


export const getVectorStoreFiles = async (vector_store_id) => {
  const url = `https://api.openai.com/v1/vector_stores/${vector_store_id}/files`;

  const headers = {
    'Authorization': `Bearer ${API_KEY}`,
    'Content-Type': 'application/json',

  };

  try {
    const response = await axios.get(url, { headers });
    return response.data.data;
  } catch (error) {
    console.error("Error obteniendo archivos de la Vector Store:", error.response ? error.response.data : error);
    return [];
  }
};

export const getFileDetails = async (fileId) => {
  const url = `https://api.openai.com/v1/files/${fileId}`;
  
  const headers = {
    'Authorization': `Bearer ${API_KEY}`,
    'Content-Type': 'application/json',

  };

  try {
    const response = await axios.get(url, { headers });
    return response.data;
  } catch (error) {
    console.error('Error obteniendo detalles del archivo:', error.response ? error.response.data : error);
    throw error;
  }
};

// Función para asociar un Vector Store a un asistente (solo Firestore)
export const associateVectorStoreToAssistant = async (assistantId, vectorStoreId) => {
  try {
    const assistantRef = doc(db, 'assistants', assistantId);
    await setDoc(assistantRef, {
      vectorStoreId,
      tool_resources: {
        file_search: {
          vector_store_ids: [vectorStoreId],
        },
      },
    }, { merge: true });
    return { id: assistantId, vectorStoreId };
  } catch (error) {
    console.error('Error al asociar el Vector Store al asistente:', error);
    throw new Error('Error al asociar el Vector Store al asistente.');
  }
};


// ── Funciones obsoletas de Assistants API (threads / runs) ──
// La Responses API no requiere threads ni runs. Se mantienen para no romper imports,
// pero no realizan ninguna acción sobre OpenAI.

export const createThread = async (messages) => {
  console.warn('createThread está obsoleto. La Responses API no requiere threads.');
  return { id: 'deprecated' };
};

export const addMessageToThread = async (threadId, message) => {
  console.warn('addMessageToThread está obsoleto. La Responses API no requiere threads.');
  return {};
};

const getMessagesFromThread = async (threadId) => {
  console.warn('getMessagesFromThread está obsoleto. La Responses API no requiere threads.');
  return "No se recibió una respuesta válida del asistente.";
};

const runThreadWithAssistant = async (threadId, assistantId) => {
  console.warn('runThreadWithAssistant está obsoleto. Usa sendMessage() con la Responses API.');
  return "No se recibió una respuesta válida del asistente.";
};

export const checkRunStatus = async (threadId, runId) => {
  console.warn('checkRunStatus está obsoleto. La Responses API no requiere runs.');
  return 'completed';
};

export const searchFilesInVectorStore = async (vectorStoreId, assistantId, query) => {
  console.warn('searchFilesInVectorStore está deprecado. La Responses API maneja file_search automáticamente.');
  return { matches: [] };
};


export const searchAndSendMessage = async (inputMessage, assistantId, vectorStoreId) => {
  // Con la Responses API, file_search se maneja automáticamente dentro de sendMessage
  return sendMessage(inputMessage, assistantId, null, []);
};


export const sendMessage = async (inputMessage, assistantId, existingThreadId = null, messages = []) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("Usuario no autenticado");

    // Obtener la API_KEY y si el usuario tiene limitación
    const { apiKey, isLimited } = await getUserApiKey(user.uid);

    // Si el usuario tiene limitación, verificamos el límite diario
    if (isLimited) {
      const estimatedCost = 0.01; // Suponiendo un costo de 0.01 USD por cada consulta
      await checkAndUpdateDailyLimit(user.uid, estimatedCost);
    }

    // Paso 1: Obtener los detalles del asistente desde Firestore
    const assistantDetails = await getAssistantDetails(assistantId);

    // Construir el input para la Responses API (sanitizado y limitado a últimos 20)
    const input = messages
      .map(sanitizeInputItemFront)
      .filter(Boolean)
      .slice(-20);

    if (inputMessage && String(inputMessage).trim()) {
      input.push({ role: 'user', content: inputMessage });
    }

    // Configurar file_search si el asistente tiene vector store
    const tools = [];
    const vectorStoreId = assistantDetails.vectorStoreId ||
      assistantDetails.tool_resources?.file_search?.vector_store_ids?.[0];

    if (vectorStoreId) {
      tools.push({ type: 'file_search', vector_store_ids: [vectorStoreId] });
    }

    const response = await callResponsesAPI({
      input,
      instructions: assistantDetails.instructions || '',
      model: assistantDetails.model || 'gpt-4o-mini',
      apiKey,
      tools: tools.length ? tools : undefined,
    });

    // Dividir la respuesta en partes más pequeñas
    const messageParts = splitAssistantResponse(response.text, 3);

    return { content: messageParts, thread_id: existingThreadId };
  } catch (error) {
    console.error("Error en el flujo completo del asistente:", error);

    // Verificar si el error es por haber excedido el límite diario
    if (error.message === 'Has excedido tu límite diario de consultas') {
      return { content: [error.message], thread_id: existingThreadId };
    }

    return { content: ["No se pudo procesar tu mensaje."], thread_id: existingThreadId };
  }
};

/**
 * Función para dividir la respuesta del asistente en partes más pequeñas.
 * Mantiene las listas completas juntas y divide solo los párrafos no relacionados.
 * 
 * @param {string} response - La respuesta completa del asistente.
 * @param {number} maxMessages - Número máximo de partes en las que dividir la respuesta.
 * @returns {Array<string>} - Array de partes divididas de la respuesta.
 */
const splitAssistantResponse = (response, maxMessages = 3) => {
  // Paso 1: Dividir la respuesta en bloques separados por dos o más saltos de línea
  const blocks = response.split(/\n\s*\n/).map(block => block.trim()).filter(block => block.length > 0);
  
  // Paso 2: Clasificar cada bloque como 'list' o 'paragraph'
  const sectionedBlocks = blocks.map(block => {
    // Detectar si el bloque es una lista (numerada o con viñetas)
    const isList = /^(\d+\.\s|[-•]\s)/m.test(block);
    return { type: isList ? 'list' : 'paragraph', content: block };
  });
  
  // Paso 3: Agrupar los bloques en mensajes, manteniendo las listas juntas
  const messages = [];
  let currentMessage = '';
  
  sectionedBlocks.forEach(section => {
    if (section.type === 'list') {
      // Si hay contenido acumulado en currentMessage, agregarlo antes de la lista
      if (currentMessage) {
        messages.push(currentMessage.trim());
        currentMessage = '';
      }
      // Agregar la lista completa como un mensaje separado
      messages.push(section.content);
    } else { // párrafo
      // Intentar acumular párrafos hasta el límite de caracteres
      if ((currentMessage + '\n\n' + section.content).length <= 500) { // Puedes ajustar el límite de caracteres
        currentMessage += (currentMessage ? '\n\n' : '') + section.content;
      } else {
        if (currentMessage) {
          messages.push(currentMessage.trim());
        }
        currentMessage = section.content;
      }
    }
  });
  
  // Agregar cualquier contenido restante en currentMessage
  if (currentMessage.trim()) {
    messages.push(currentMessage.trim());
  }
  
  // Paso 4: Si el número de mensajes excede el máximo permitido, agrupar los últimos mensajes
  if (messages.length > maxMessages) {
    const groupedMessages = [];
    const messagesPerGroup = Math.ceil(messages.length / maxMessages);
    for (let i = 0; i < messages.length; i += messagesPerGroup) {
      groupedMessages.push(messages.slice(i, i + messagesPerGroup).join('\n\n'));
    }
    return groupedMessages.slice(0, maxMessages);
  }
  
  return messages;
};


// Función para actualizar el nombre del asistente en Firestore
export const updateAssistantName = async (assistantId, newName, apiKey) => {
  try {
    const assistantRef = doc(db, 'assistants', assistantId);
    await setDoc(assistantRef, { name: newName }, { merge: true });
    return { id: assistantId, name: newName };
  } catch (error) {
    console.error("Error actualizando el nombre del asistente:", error);
    throw error;
  }
};



// Función para editar las instrucciones del asistente
// Función para editar las instrucciones del asistente (solo Firestore)
export const updateAssistantInstructions = async (assistantId, newInstructions) => {
  try {
    const assistantRef = doc(db, 'assistants', assistantId);
    await setDoc(assistantRef, { instructions: newInstructions }, { merge: true });
    return { id: assistantId, instructions: newInstructions };
  } catch (error) {
    console.error(
      'Error actualizando las instrucciones del asistente:',
      error
    );
    throw error;
  }
};


// Función para habilitar file_search para un asistente (solo Firestore)
export const enableFileSearchForAssistant = async (assistantId) => {
  try {
    const assistantRef = doc(db, 'assistants', assistantId);
    await setDoc(assistantRef, { tools: [{ type: "file_search" }] }, { merge: true });
    return { id: assistantId, tools: [{ type: "file_search" }] };
  } catch (error) {
    console.error('Error habilitando file search:', error);
    throw error;
  }
};

// Función para deshabilitar file_search para un asistente (solo Firestore)
export const disableFileSearchForAssistant = async (assistantId) => {
  try {
    const assistantRef = doc(db, 'assistants', assistantId);
    await setDoc(assistantRef, { tools: [] }, { merge: true });
    return { id: assistantId, tools: [] };
  } catch (error) {
    console.error('Error deshabilitando file search:', error);
    throw error;
  }
};
